import { Injectable, signal, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IMicaChatTelefonoData } from '@core/interfaces/entidades/telefono.entidad';
import { MicaChat_ChatsEntity } from '@modules/mica-chat/entities/chats.mica-chat';
import { IEvolutionEstadoConexion } from '@core/services/api/evolution-api.service';
import { MicaChatListenerService } from '@shared/services/websocket/mica-chat-listener.service';
import { EVENT_GATEWAY } from '@core/enums/eventWebSocket.enum';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MicaChatService {
  private readonly listenerService = inject(MicaChatListenerService);
  private readonly destroyRef = inject(DestroyRef);

  protected telefonoSeleccionado = new BehaviorSubject<IMicaChatTelefonoData | null>(null);
  protected chatSeleccionado = new BehaviorSubject<MicaChat_ChatsEntity | null>(null);

	readonly telefonoSeleccionadoSignal = signal<IMicaChatTelefonoData | null>(null);

	private readonly _estadoConexion = signal<IEvolutionEstadoConexion | null>(null);
	readonly estadoConexion = this._estadoConexion.asReadonly();

	private readonly _presenciaChat = signal<{ remoteJid: string; presencia: string } | null>(null);
	readonly presenciaChat = this._presenciaChat.asReadonly();

	constructor() {
		this.listenerService
			.getData<{ telefono_id: number; estado: string; qr?: string | null }>(
				EVENT_GATEWAY.MICA_CHAT_CONEXION_ACTUALIZADA,
			)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((data) => {
				if (data?.estado) {
					this._estadoConexion.set({
						estado: data.estado as IEvolutionEstadoConexion['estado'],
						qr: data.qr ?? this._estadoConexion()?.qr ?? null,
					});
				}
			});

		this.listenerService
			.getData<{ remoteJid: string; telefono_id: number; presencia: string }>(
				EVENT_GATEWAY.MICA_CHAT_PRESENCIA_ACTUALIZADA,
			)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((data) => {
				if (data?.remoteJid) {
					const currentChat = this.chatSeleccionado.getValue();
					if (
						currentChat?.remoteJid === data.remoteJid ||
						currentChat?.id === data.remoteJid
					) {
						this._presenciaChat.set({
							remoteJid: data.remoteJid,
							presencia: data.presencia,
						});
						if (data.presencia !== 'composing') {
							setTimeout(() => {
								const p = this._presenciaChat();
								if (p?.remoteJid === data.remoteJid && p?.presencia === data.presencia) {
									this._presenciaChat.set(null);
								}
							}, 5000);
						}
					}
				}
			});
	}

  get telefonoSeleccionado$() {
    return this.telefonoSeleccionado.asObservable();
  }
  get chatSeleccionado$() {
    return this.chatSeleccionado.asObservable();
  }

  getTelefonoSeleccionado() {
    return this.telefonoSeleccionado.getValue();
  }

  setTelefonoSeleccionado(telefono: IMicaChatTelefonoData | null) {
    this.telefonoSeleccionado.next(telefono);
    this.telefonoSeleccionadoSignal.set(telefono);
  }

  getChatSeleccionado() {
    return this.chatSeleccionado.getValue();
  }

	setChatSeleccionado(chat: MicaChat_ChatsEntity | null) {
		this.chatSeleccionado.next(chat);
		this._presenciaChat.set(null);
	}

  setEstadoConexion(estado: IEvolutionEstadoConexion): void {
    this._estadoConexion.set(estado);
  }

	clearEstadoConexion(): void {
		this._estadoConexion.set(null);
	}

	clearPresenciaChat(): void {
		this._presenciaChat.set(null);
	}
}
